import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import IIntegratorFirm, {
  IntegratorDefaultIsSearchable,
  IntegratorFeedAuth,
  IntegratorSchemaMode,
} from "../../../interfaces/models/IIntegratorFirm";
import { INTEGRATOR_DEFAULT_IS_SEARCHABLE_VALUES } from "../../../interfaces/models/IIntegratorSettings";
import {
  getIntegratorFirmByFirmId,
  createIntegratorFirm,
  updateIntegratorFirm,
  deleteIntegratorFirm,
  probeIntegratorFirmFeed,
} from "../../../Services/IntegratorFirm";
import { getFirmById } from "../../../Services/Firm";
import { firmShortLabel } from "../../../utils/firmDisplayName";
import { useDynamicPageMeta } from "../../../hooks/useDynamicPageMeta";
import Input from "../../../components/Atomos/Inputs/Input";
import SwitchToggle from "../../../components/Atomos/Switch-Toogle/Switch";
import DropDownList from "../../../components/Atomos/DropDown/Dropdown";
import Button from "../../../components/staff/Button";
import CheckBox from "../../../components/staff/CheckBox";
import type { DropDownOptionBase } from "../../../components/staff/DropDownMultiple";
import Modal from "../../../components/Atomos/Modals/Modal";
import Close from "../../../components/Atomos/Icons/Close";
import SaveChanges from "../../../components/Atomos/Icons/EditMode/SaveChanges";
import Search from "../../../components/Atomos/Icons/Search";
import { toastError, toastSuccess } from "../../../utils/toast";
import EntityAttributeGrid from "./feedMap/EntityAttributeGrid";
import FeedAuthFields, {
  emptyFeedAuthForm,
  feedAuthFormFromConfig,
  feedAuthFromForm,
  type FeedAuthForm,
} from "./FeedAuthFields";
import {
  OFFICE_FEED_MAP_FIELDS,
  PRACTICE_AREA_FEED_MAP_FIELDS,
  PROFESSIONAL_FEED_MAP_FIELDS,
} from "./feedMap/feedMapFieldCatalog";
import {
  ancestorAndSelfPaths,
  emptyFeedMapSelections,
  feedDictionaryFromSelections,
  keepPathsInsideCollection,
  pathsFromFeedDictionary,
  selectionsFromFeedDictionary,
  setCollectionPath,
  setFieldPath,
  type FeedMapCollectionKey,
  type FeedMapSelections,
} from "./feedMap/feedMapDictionary";

const SCHEMA_OPTIONS: { id: IntegratorSchemaMode; name: string }[] = [
  { id: "standard", name: "Standard" },
  { id: "custom", name: "Custom (legacy)" },
];

const DEFAULT_IS_SEARCHABLE_OPTIONS: {
  id: IntegratorDefaultIsSearchable;
  name: string;
}[] = [
  { id: "true", name: "True" },
  { id: "false", name: "False" },
  { id: "notSet", name: "[Not Set]" },
];

function isDefaultIsSearchable(
  value: string | undefined
): value is IntegratorDefaultIsSearchable {
  return (
    INTEGRATOR_DEFAULT_IS_SEARCHABLE_VALUES as readonly string[]
  ).includes(value ?? "");
}

const LIST_ROUTE = "/integrator-engine/firms";
const FOOTER_BUTTON_CLASS =
  "w-full justify-center sm:w-[12.5rem] md:w-[12.5rem] lg:w-[12.5rem] shrink-0";
const DEFAULT_FEED_TIMEOUT_SECONDS = 45;

function feedTimeoutMsFromSeconds(raw: string): number | undefined {
  const seconds = Number(String(raw).trim());
  return Number.isInteger(seconds) && seconds > 0 ? seconds * 1000 : undefined;
}

function feedTimeoutSecondsFromMs(timeoutMs: number | undefined): string {
  if (!timeoutMs || timeoutMs <= 0) {
    return "";
  }
  return String(Math.round(timeoutMs / 1000));
}

function feedPathOptionsFromFields(fields: string[]): DropDownOptionBase[] {
  const unique = [...new Set(fields.map((path) => path.trim()).filter(Boolean))];
  return unique.map((path) => ({ id: path, name: path }));
}

function probeRequestError(error: any): string {
  const body = error?.response?.data;
  if (typeof body === "string" && body.trim()) {
    return body;
  }
  return error?.message ?? "Could not probe the feed.";
}

async function probeReadableFeed(
  firmId: string,
  feedUrl: string,
  feedAuth?: IntegratorFeedAuth,
  feedTimeoutMs?: number,
  skipSslVerification?: boolean
) {
  const result = await probeIntegratorFirmFeed({
    firmId,
    feedUrl,
    feedAuth,
    feedTimeoutMs,
    skipSslVerification,
  });
  if (!result.canFetch) {
    throw new Error(result.fetchError?.trim() || "Could not fetch the feed.");
  }
  if (!result.canRead) {
    throw new Error(result.readError?.trim() || "Could not read the feed.");
  }
  return result;
}

export default function EditIntegratorFirm(): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: firmId } = useParams();
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();

  const locationState = location.state as Record<string, any> | null;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [probing, setProbing] = useState(false);
  const probeInFlight = useRef(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [existing, setExisting] = useState<IIntegratorFirm | undefined>();
  const [firmName, setFirmName] = useState<string>(
    locationState?.name ?? locationState?.firmName ?? ""
  );

  // Form state
  const [enabled, setEnabled] = useState(false);
  const [feedUrl, setFeedUrl] = useState("");
  const [schemaMode, setSchemaMode] = useState<IntegratorSchemaMode>("standard");
  const [feedPathOptions, setFeedPathOptions] = useState<DropDownOptionBase[]>(
    []
  );
  const [feedMapSelections, setFeedMapSelections] = useState<FeedMapSelections>(
    emptyFeedMapSelections()
  );
  const [feedAuth, setFeedAuth] = useState<FeedAuthForm>(emptyFeedAuthForm());
  const [feedTimeoutSeconds, setFeedTimeoutSeconds] = useState("");
  const [defaultIsSearchable, setDefaultIsSearchable] =
    useState<IntegratorDefaultIsSearchable>("notSet");
  const [importProfessionalPicture, setImportProfessionalPicture] =
    useState(false);
  const [skipSslVerification, setSkipSslVerification] = useState(false);

  const probeUrl = useCallback(async (
    url: string,
    auth?: IntegratorFeedAuth,
    timeoutMs?: number,
    skipSsl?: boolean
  ) => {
    const trimmed = url.trim();
    if (!firmId || !trimmed || probeInFlight.current) {
      return;
    }
    probeInFlight.current = true;
    setProbing(true);
    try {
      const result = await probeReadableFeed(
        firmId,
        trimmed,
        auth ?? feedAuthFromForm(feedAuth),
        timeoutMs ?? feedTimeoutMsFromSeconds(feedTimeoutSeconds),
        skipSsl ?? skipSslVerification
      );
      setFeedPathOptions((current) =>
        feedPathOptionsFromFields([
          ...result.fields,
          ...current.map((option) => String(option.id)),
        ])
      );
      toastSuccess("Fetch: ✅, Read: ✅");
    } catch (error: any) {
      toastError(probeRequestError(error));
    } finally {
      probeInFlight.current = false;
      setProbing(false);
    }
  }, [firmId, feedAuth, feedTimeoutSeconds, skipSslVerification]);

  // Load existing config (presence = configured) + firm name fallback.
  useEffect(() => {
    if (!firmId) {
      setLoadError(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      let urlToProbe = "";
      let authToProbe: IntegratorFeedAuth | undefined;
      let timeoutToProbe: number | undefined;
      let skipSslToProbe: boolean | undefined;
      try {
        const config = await getIntegratorFirmByFirmId(firmId);
        if (cancelled) {
          return;
        }
        if (config) {
          setExisting(config);
          setEnabled(Boolean(config.enabled));
          setFeedUrl(config.feedUrl ?? "");
          setSchemaMode(config.schemaMode ?? "standard");
          const selections = selectionsFromFeedDictionary(config.feedMap);
          setFeedMapSelections(selections);
          const savedFeedFields = (config.feedFields ?? []).filter((path) =>
            String(path).trim()
          );
          if (savedFeedFields.length > 0) {
            setFeedPathOptions(feedPathOptionsFromFields(savedFeedFields));
          } else {
            setFeedPathOptions(
              feedPathOptionsFromFields(pathsFromFeedDictionary(config.feedMap))
            );
            urlToProbe = (config.feedUrl ?? "").trim();
            authToProbe = config.feedAuth;
            timeoutToProbe = config.feedTimeoutMs;
            skipSslToProbe = config.skipSslVerification === true;
          }
          setFeedAuth(feedAuthFormFromConfig(config.feedAuth));
          setFeedTimeoutSeconds(feedTimeoutSecondsFromMs(config.feedTimeoutMs));
          setSkipSslVerification(config.skipSslVerification === true);
          setDefaultIsSearchable(
            isDefaultIsSearchable(config.defaultIsSearchable)
              ? config.defaultIsSearchable
              : "notSet"
          );
          setImportProfessionalPicture(config.importProfessionalPicture === true);
        }
        if (!firmName) {
          const firm = await getFirmById(firmId);
          if (!cancelled && firm) {
            setFirmName(firmShortLabel(firm));
          }
        }
      } catch {
        if (!cancelled) {
          setLoadError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
      if (!cancelled && urlToProbe) {
        await probeUrl(urlToProbe, authToProbe, timeoutToProbe, skipSslToProbe);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmId]);

  // Dynamic banner title + breadcrumb label (last crumb = firm name).
  useEffect(() => {
    setPageMeta({
      title: firmName || "Firm Configuration",
      breadcrumbLabels: firmId
        ? {
            [`/integrator-engine/firms/${firmId}`.toLowerCase()]:
              firmName || "Configure",
          }
        : {},
    });
    return () => clearPageMeta();
  }, [firmId, firmName, setPageMeta, clearPageMeta]);

  const handleCheckUrl = useCallback(() => {
    void probeUrl(feedUrl);
  }, [probeUrl, feedUrl]);

  const fieldPathOptions = useMemo(
    () =>
      feedPathOptionsFromFields(
        ancestorAndSelfPaths([
          ...feedPathOptions.map((option) => String(option.id)),
          ...Object.values(feedMapSelections.collectionPaths),
        ])
      ),
    [feedPathOptions, feedMapSelections.collectionPaths]
  );

  const handleFeedPathChange = useCallback(
    (
      section: Exclude<keyof FeedMapSelections, "collectionPaths">,
      fieldKey: string,
      feedPath: string | null
    ) => {
      setFeedMapSelections((current) => ({
        ...current,
        [section]: setFieldPath(current[section], fieldKey, feedPath),
      }));
    },
    []
  );

  const handleCollectionPathChange = useCallback(
    (section: FeedMapCollectionKey, feedPath: string | null) => {
      setFeedMapSelections((current) => ({
        ...current,
        collectionPaths: setCollectionPath(
          current.collectionPaths,
          section,
          feedPath
        ),
        [section]: keepPathsInsideCollection(current[section], feedPath ?? ""),
      }));
    },
    []
  );

  const handleSave = async () => {
    if (!firmId || saving) {
      return;
    }
    setSaving(true);
    try {
      const base: Partial<IIntegratorFirm> = existing ?? {};
      const payload = {
        ...base,
        firmId,
        enabled,
        feedUrl: feedUrl.trim(),
        schemaMode,
        feedAuth: feedAuthFromForm(feedAuth),
        feedTimeoutMs: feedTimeoutMsFromSeconds(feedTimeoutSeconds),
        skipSslVerification,
        defaultIsSearchable,
        importProfessionalPicture,
        feedMap: feedDictionaryFromSelections({
          selections: feedMapSelections,
          existingFirmMap: existing?.feedMap?.firm,
        }),
      };
      if (existing) {
        const updated = await updateIntegratorFirm(payload as IIntegratorFirm);
        setExisting(updated);
        toastSuccess("Configuration updated.");
      } else {
        await createIntegratorFirm(payload as IIntegratorFirm);
        toastSuccess("Firm configured.");
      }
      navigate(LIST_ROUTE);
    } catch (e: any) {
      toastError(e?.message ?? "Could not save the configuration.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!existing?.id || removing) {
      return;
    }
    setRemoving(true);
    try {
      await deleteIntegratorFirm(existing.id);
      toastSuccess("Configuration removed.");
      setConfirmOpen(false);
      navigate(LIST_ROUTE);
    } catch (e: any) {
      toastError(e?.message ?? "Could not remove the configuration.");
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center px-4 text-gray-600">
        Loading…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-gray-700">Could not load this firm's configuration.</p>
        <Button
          className="w-full max-w-xs sm:w-auto sm:max-w-none"
          text="Back to Import Module Dashboard"
          color="secondary"
          variant="filled"
          size="fixed"
          onClick={() => navigate(LIST_ROUTE)}
        />
      </div>
    );
  }

  return (
    <>
      <Modal
        isModalOpen={confirmOpen}
        toggleModal={() => !removing && setConfirmOpen(false)}
        title="Remove configuration"
        titleColor="text-secondary font-semibold text-lg"
        compact
        outerPaddingOnly
        compactBodyScrolls={false}
        className="w-full p-10 !max-w-md !overflow-visible border border-gray-200 shadow-lg sm:!max-w-lg"
        bodyClassName="!pt-0"
      >
        <div className="flex flex-col gap-4">
          <p className="text-left text-sm leading-relaxed text-[#474F56] sm:text-base">
            Are you sure you want to remove the Integrator configuration for this
            firm? The firm will no longer participate in the sync until it is
            configured again.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
            <Button
              className={FOOTER_BUTTON_CLASS}
              text="No, go back"
              onClick={() => !removing && setConfirmOpen(false)}
              color="neutral"
              variant="outlined"
              size="fixed"
              icon={<Close />}
              disabled={removing}
            />
            <Button
              className={FOOTER_BUTTON_CLASS}
              text={removing ? "Removing…" : "Yes, remove"}
              onClick={() => void handleRemove()}
              color="secondary"
              variant="filled"
              size="fixed"
              disabled={removing}
            />
          </div>
        </div>
      </Modal>

      <div className="w-full overflow-hidden rounded-lg">
        <div className="flex h-16 items-center bg-accent-Default-dark px-6">
          <span className="rounded-lg bg-accent-Default-light px-4 py-2.5 text-base font-medium text-accent-Default-dark">
            Integrator Configuration
          </span>
        </div>

        <div className="flex flex-col gap-10 bg-white p-6">
          <section className="flex flex-col gap-6">
            <SwitchToggle
              title="Integrator Enabled for this Firm"
              checked={enabled}
              seter={setEnabled}
            />

            <div className="flex w-full flex-col gap-6 lg:flex-row lg:items-end">
              <div className="min-w-0 flex-1">
                <Input
                  title="URL"
                  name="feedUrl"
                  value={feedUrl}
                  placeholder="https://example.com/feed.xml"
                  onChange={(e) => setFeedUrl(e.target.value)}
                  error={false}
                  isWfull
                />
              </div>
              <Button
                className="w-full justify-center whitespace-nowrap lg:w-auto lg:shrink-0"
                text={probing ? "Checking…" : "Check / Review URL"}
                onClick={() => void handleCheckUrl()}
                color="secondary"
                variant="filled"
                size="fixed"
                icon={<Search />}
                disabled={!feedUrl.trim() || saving || removing || probing}
              />
            </div>

            <div className="flex w-full flex-col gap-6 lg:flex-row lg:items-start">
              <div className="w-full max-w-[10rem] shrink-0">
                <Input
                  title="Timeout"
                  name="feedTimeoutSeconds"
                  type="number"
                  value={feedTimeoutSeconds}
                  placeholder={String(DEFAULT_FEED_TIMEOUT_SECONDS)}
                  onChange={(e) => setFeedTimeoutSeconds(e.target.value)}
                  error={false}
                  isWfull
                />
              </div>
              <FeedAuthFields
                embedded
                value={feedAuth}
                onChange={setFeedAuth}
              />
            </div>

            <CheckBox
              name="skipSslVerification"
              label="Skip SSL Verification"
              checked={skipSslVerification}
              onCheckedChange={setSkipSslVerification}
              disabled={saving || removing || probing}
            />

            <div className="flex w-full flex-col gap-6 lg:flex-row lg:items-end">
              <div className="min-w-0 flex-1">
                <DropDownList
                  title="Schema mode"
                  getid
                  disableInput
                  data={SCHEMA_OPTIONS}
                  selectedObjst={
                    SCHEMA_OPTIONS.find((o) => o.id === schemaMode)?.name
                  }
                  seter={(v: { id: IntegratorSchemaMode; name: string }) =>
                    setSchemaMode(v?.id ?? "standard")
                  }
                />
              </div>
              <div className="min-w-0 flex-1">
                <DropDownList
                  title="Default Person Searchability"
                  getid
                  disableInput
                  data={DEFAULT_IS_SEARCHABLE_OPTIONS}
                  selectedObjst={
                    DEFAULT_IS_SEARCHABLE_OPTIONS.find(
                      (option) => option.id === defaultIsSearchable
                    )?.name
                  }
                  seter={(v: {
                    id: IntegratorDefaultIsSearchable;
                    name: string;
                  }) =>
                    setDefaultIsSearchable(
                      isDefaultIsSearchable(v?.id) ? v.id : "notSet"
                    )
                  }
                />
              </div>
              <div className="min-w-0 flex-1">
                <SwitchToggle
                  title="Import Professional Picture"
                  checked={importProfessionalPicture}
                  seter={setImportProfessionalPicture}
                />
              </div>
            </div>
          </section>

          {schemaMode === "custom" && (
            <section className="flex flex-col gap-6">
              <h2 className="text-xl font-bold text-gray-font">
                Label Dictionary
              </h2>
              <div className="grid grid-cols-1 divide-y divide-gray-100 border-t border-gray-100 lg:grid-cols-3 lg:divide-x lg:divide-y-0">
                <EntityAttributeGrid
                  title="Professional"
                  fields={PROFESSIONAL_FEED_MAP_FIELDS}
                  mapping={{
                    selectedPaths: feedMapSelections.professional,
                    options: fieldPathOptions,
                    onPathChange: (fieldKey, feedPath) =>
                      handleFeedPathChange("professional", fieldKey, feedPath),
                  }}
                  collectionPath={{
                    options: fieldPathOptions,
                    selectedPath: feedMapSelections.collectionPaths.professional,
                    onPathChange: (feedPath) =>
                      handleCollectionPathChange("professional", feedPath),
                  }}
                />
                <EntityAttributeGrid
                  title="Office"
                  fields={OFFICE_FEED_MAP_FIELDS}
                  mapping={{
                    selectedPaths: feedMapSelections.office,
                    options: fieldPathOptions,
                    onPathChange: (fieldKey, feedPath) =>
                      handleFeedPathChange("office", fieldKey, feedPath),
                  }}
                  collectionPath={{
                    options: fieldPathOptions,
                    selectedPath: feedMapSelections.collectionPaths.office,
                    onPathChange: (feedPath) =>
                      handleCollectionPathChange("office", feedPath),
                  }}
                />
                <EntityAttributeGrid
                  title="Practice area"
                  fields={PRACTICE_AREA_FEED_MAP_FIELDS}
                  mapping={{
                    selectedPaths: feedMapSelections.practiceArea,
                    options: fieldPathOptions,
                    onPathChange: (fieldKey, feedPath) =>
                      handleFeedPathChange("practiceArea", fieldKey, feedPath),
                  }}
                  collectionPath={{
                    options: fieldPathOptions,
                    selectedPath: feedMapSelections.collectionPaths.practiceArea,
                    onPathChange: (feedPath) =>
                      handleCollectionPathChange("practiceArea", feedPath),
                  }}
                />
              </div>
            </section>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              {existing && (
                <Button
                  className={FOOTER_BUTTON_CLASS}
                  text="Remove configuration"
                  color="neutral"
                  variant="outlined"
                  size="fixed"
                  onClick={() => setConfirmOpen(true)}
                  disabled={saving || removing}
                />
              )}
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
              <Button
                className={FOOTER_BUTTON_CLASS}
                text="Cancel"
                onClick={() => navigate(LIST_ROUTE)}
                color="neutral"
                variant="outlined"
                size="fixed"
                icon={<Close />}
                disabled={saving || removing}
              />
              <Button
                className={FOOTER_BUTTON_CLASS}
                text={saving ? "Saving…" : "Save Changes"}
                onClick={() => void handleSave()}
                color="secondary"
                variant="filled"
                size="fixed"
                icon={<SaveChanges />}
                disabled={saving || removing}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
